import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel, CarouselGameCard, HeadingCarousel, normalizeCarouselGame} from "@/components";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import useListFavoriteGames from "@/hooks/games/useListFavoriteGame.hook";
import useGetUserInfo from "@/hooks/useGetUserInfo";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./favorite-games-carousel.styles.module.css";

const FAVORITE_CAROUSEL_SKELETON_KEYS = [
  "fav-carousel-skel-0",
  "fav-carousel-skel-1",
  "fav-carousel-skel-2",
  "fav-carousel-skel-3",
  "fav-carousel-skel-4",
  "fav-carousel-skel-5",
];

export const FavoriteGamesCarousel = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [carouselApi, setCarouselApi] = useState(null);

  const authStatus = useAuthStore(state => state.authStatus);
  const {data: userData} = useGetUserInfo();
  const {listFavGames, isLoading} = useListFavoriteGames();
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const alias = userData?.alias || userData?.given_name || "";

  const favoriteGames = useMemo(() => {
    const apiList = listFavGames?.data?.data ?? [];
    const favIds = storeFavoriteGames.length > 0 ? storeFavoriteGames : apiList;
    if (favIds.length === 0) return [];
    return favIds.slice(0, 10).map(fav => {
      const apiFav = apiList.find(a => a.game_id === fav.game_id);
      return normalizeCarouselGame(
        {
          ...apiFav,
          ...fav,
          id: fav.game_id,
          provider: fav.game_provider || apiFav?.game_provider,
          game_provider: fav.game_provider || apiFav?.game_provider,
          name: fav.name || apiFav?.game_name || fav.game_name,
          icon: fav.icon || apiFav?.icon,
        },
        getGameById
      );
    });
  }, [storeFavoriteGames, listFavGames, getGameById]);

  const isGameFavorited = useCallback(
    gameId => storeFavoriteGames.some(f => f.game_id === gameId),
    [storeFavoriteGames]
  );

  const handleFavorite = useCallback(
    game => {
      setFavoriteGames(
        {id: game.id, provider: game.provider, game_provider: game.provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  const handleGameClick = useCallback(
    (provider, gameId) => {
      navigate(`/games/${provider}/${gameId}`);
    },
    [navigate]
  );

  if (authStatus !== "authenticated" || (!isLoading && favoriteGames.length === 0)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <HeadingCarousel
        title={alias ? t("homeFavoritesCarouselTitle", {alias}) : t("homeFavoritesCarouselTitleGeneric")}
        subtitle={t("homeFavoritesCarouselSubTitle")}
        icon="❤️"
        viewAllLink="/favorites"
        showArrowsOnMobile
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />
      <Carousel
        autoScroll={false}
        onInit={setCarouselApi}
        renderItems={() =>
          isLoading
            ? FAVORITE_CAROUSEL_SKELETON_KEYS.map(skelKey => (
                <CarouselGameCard
                  key={skelKey}
                  loading
                />
              ))
            : favoriteGames.map(game => (
                <CarouselGameCard
                  key={`fav-${game.id}-${game.provider}`}
                  name={game.name}
                  icon={game.icon}
                  providerLabel={game.providerLabel}
                  isFav={isGameFavorited(game.id)}
                  onFavorite={() => handleFavorite(game)}
                  favoriteAddLabel={t("favoritesAdd")}
                  favoriteRemoveLabel={t("favoritesRemove")}
                  onClick={() => handleGameClick(game.provider, game.id)}
                />
              ))
        }
      />
    </div>
  );
};
